/**
 * Practice whitelabeling (light): a practice picks one brand colour and the
 * patient-facing pages derive every primary-family token in tokens.css from it.
 * Neutrals, status colours and the Dermalink logo stay as they are.
 */

/** The Dermalink default, i.e. --c-primary in tokens.css. */
export const DEFAULT_BRAND_COLOR = '#ec6a4c';

/**
 * Minimum contrast of white text on the brand colour (buttons, active pills).
 * Matches what the Dermalink default itself reaches (≈3.1:1, WCAG AA for large
 * and bold text); a paler pick is darkened until it gets there.
 */
export const MIN_CONTRAST_ON_WHITE = 3;

/** Example themes offered in the practice settings; any other colour can be picked too. */
export const BRAND_COLOR_PRESETS: { name: string; color: string }[] = [
  { name: 'Dermalink', color: DEFAULT_BRAND_COLOR },
  { name: 'Salie',     color: '#5b8a72' },
  { name: 'Lagune',    color: '#2a8c8c' },
  { name: 'Oceaan',    color: '#2f6690' },
  { name: 'Aubergine', color: '#7a4a7e' },
  { name: 'Roos',      color: '#c25b7a' },
  { name: 'Oker',      color: '#b07d2b' },
  { name: 'Antraciet', color: '#3f4a54' },
];

type Rgb = [number, number, number];

function toRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

/** `weight` of `hex` over `other`, like CSS color-mix(in srgb, hex weight, other). */
function mix(hex: string, other: string, weight: number): string {
  const a = toRgb(hex);
  const b = toRgb(other);
  return toHex([0, 1, 2].map((i) => a[i] * weight + b[i] * (1 - weight)) as Rgb);
}

function rgba(hex: string, alpha: number): string {
  return `rgba(${toRgb(hex).join(', ')}, ${alpha})`;
}

function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastWithWhite(hex: string): number {
  return 1.05 / (luminance(hex) + 0.05);
}

/** The brand colour as actually used: darkened in small steps until white text on it stays legible. */
export function legibleBrandColor(hex: string): string {
  let color = hex.toLowerCase();
  for (let i = 0; i < 20 && contrastWithWhite(color) < MIN_CONTRAST_ON_WHITE; i++) {
    color = mix(color, '#000000', 0.94);
  }
  return color;
}

/** CSS custom properties overriding the primary family in tokens.css for this brand colour. */
export function practiceThemeTokens(brandColor: string): Record<string, string> {
  const c      = legibleBrandColor(brandColor);
  const dark   = mix(c, '#000000', 0.85);
  const active = mix(c, '#000000', 0.8);

  return {
    '--c-primary':             c,
    '--c-primary-hover':       mix(c, '#ffffff', 0.85),
    '--c-primary-dark':        dark,
    '--c-primary-active':      active,
    '--c-primary-tint-bg':     rgba(c, 0.08),
    '--c-primary-tint-border': rgba(c, 0.45),
    '--c-primary-tint-ring':   rgba(c, 0.6),
    '--c-border-primary':      mix(c, '#ffffff', 0.55),
    '--c-nav-hover':           mix(c, '#ffffff', 0.8),
    '--c-avatar-ring':         mix(c, '#ffffff', 0.9),
    // Warm surfaces are tuned to the default salmon; tint a neutral with the brand instead.
    '--c-bg-app':              mix(c, '#f6f5f4', 0.06),
    '--c-bg-rail':             mix(c, '#fbfbfa', 0.04),
    '--c-bg-warm':             mix(c, '#ffffff', 0.07),
    '--c-bg-selected':         mix(c, '#ffffff', 0.05),
    '--gradient-brand-bg':     `linear-gradient(135deg, ${mix(c, '#ffffff', 0.12)} 0%, ${mix(c, '#ffffff', 0.05)} 60%, ${mix(c, '#ffffff', 0.1)} 100%)`,
    '--gradient-doctor-hero':  `linear-gradient(135deg, ${mix(c, '#ffffff', 0.92)} 0%, ${mix(c, '#000000', 0.93)} 100%)`,
    '--shadow-cta':            `0 8px 20px ${rgba(dark, 0.3)}`,
  };
}
